import { useMemo } from 'react';
import { NavLink, useParams } from 'react-router-dom';
import moment from 'moment-timezone';
import { LoginEventType } from '@cbinsights/userv2service/userv2service';
import { LoginEventProperties } from '@cbinsights/adminservice/adminservice';
import { CbiEntityRoutes } from 'client/modules/cbi-entity/RoutesHelpers';
import { useGetUserTrialQuery } from 'client/modules/trial-package/services/hooks/useGetUserTrialQuery';
import { useGetPackagesQuery } from 'client/modules/trial-package/services/hooks/useGetPackagesQuery';
import UTCTooltip from 'client/utils/dates';
import { useGetUserLoginHistory } from '../services/hooks/useGetUserLoginHistory';
import { useGetUserQuery } from '../services/hooks/useGetUserQuery';
import { useGetUserPackageHistory } from '../services/hooks/useGetUserPackageHistory';
import { TrialInfo } from '../types/trial';

const getLoginEventDetails = (
  properties: LoginEventProperties | undefined
): { label: string; value: string }[] | undefined => {
  if (!properties) {
    return undefined;
  }
  if (properties.oauth) {
    return [{ label: 'Application', value: properties.oauth.application_name }];
  }
  return undefined;
};

export const loginHistory = {
  columns: [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => <UTCTooltip date={date} />,
    },
    {
      title: 'Success/Failure',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        if (status === LoginEventType.LOGIN_SUCCESS) {
          return <span className="text-green-500">Successful Login</span>;
        }
        if (status === LoginEventType.LOGOUT) {
          return <span className="text-yellow-500">Logout</span>;
        }
        if (status === LoginEventType.UNKNOWN_LOGIN_EVENT) {
          return <span>Unknow login Event</span>;
        }
        if (status === LoginEventType.LOGIN_FAILURE) {
          return <span className="text-red-500">Login Failure</span>;
        }
        return <span>{status}</span>;
      },
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'Details',
      dataIndex: 'details',
      key: 'details',
      render: (details: { label: string; value: string }[] | undefined) => {
        if (!details || details.length === 0) {
          return <span>-</span>;
        }
        return (
          <ul className="m-0 pl-0 list-none">
            {details.map(({ label, value }) => (
              <li key={label}>
                <span className="font-semibold">{label}:</span> {value}
              </li>
            ))}
          </ul>
        );
      },
    },
  ],
};
export const packageHistory = {
  columns: [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (date: string) => <UTCTooltip date={date} />,
    },
    {
      title: 'Package',
      dataIndex: 'package',
      key: 'package',
    },
    {
      title: 'Start Date',
      dataIndex: 'startDate',
      key: 'startDate',
    },
    {
      title: 'End Date',
      dataIndex: 'endDate',
      key: 'endDate',
    },
    {
      title: 'Action By',
      dataIndex: 'actionBy',
      key: 'actionBy',
      render: (text, { actionById }) => (
        <NavLink
          to={`/users/${actionById}`}
          target="_blank"
          rel="noopener noreferrer"
          className="type--link"
        >
          {text}
        </NavLink>
      ),
    },
  ],
};

export const useGetUserInformation = () => {
  const { id } = useParams<typeof CbiEntityRoutes.Id>();

  const userId = useMemo(() => {
    const value = Number.parseInt(id, 10);

    if (Number.isNaN(value)) {
      return null;
    }

    return value;
  }, [id]);

  const {
    data,
    isLoading,
    refetch: refetchUserQuery,
  } = useGetUserQuery({
    enabled: Boolean(userId),
    params: { id_user: userId },
  });

  const { data: userTrialsInfo } = useGetUserTrialQuery({
    enabled: !!userId,
    params: {
      id_user: userId,
    },
  });

  const { data: packagesData } = useGetPackagesQuery({
    enabled: true,
    params: {},
  });

  const { data: loginHistoryData } = useGetUserLoginHistory({
    enabled: true,
    params: {
      id_user: userId,
    },
  });

  const { data: packageHistoryData, refetch: refetchPackageHistory } =
    useGetUserPackageHistory({
      enabled: true,
      params: {
        id_user: userId,
      },
    });

  const refetchUserInformation = () => {
    refetchUserQuery();
    refetchPackageHistory();
  };

  const packageHistorySource = useMemo(() => {
    if (!packageHistoryData?.user_package_histories) {
      return [];
    }

    return packageHistoryData.user_package_histories.map((entry) => ({
      date: entry.ts_log,
      package: entry.package_name,
      startDate: moment(entry.package_start).format('MM/DD/YYYY'),
      endDate: moment(entry.package_end).format('MM/DD/YYYY'),
      actionBy: entry.first_name
        ? `${entry.first_name} ${entry.last_name}`
        : entry.initiator_email,
      actionById: entry.initiator_id,
    }));
  }, [packageHistoryData]);

  const userTrialInfo: TrialInfo = useMemo(() => {
    const activeTrial = userTrialsInfo?.user_trials.find((trial) => {
      return (
        moment(trial.ts_trial_package_end).isAfter(moment().startOf('day')) &&
        trial.id_user === userId
      );
    });

    if (
      !userTrialsInfo ||
      userTrialsInfo?.user_trials.length === 0 ||
      !packagesData ||
      !activeTrial
    ) {
      return {
        packageName: 'N/A',
        originalPackageName: 'N/A',
        startDate: 'N/A',
        endDate: 'N/A',
        isActive: false,
      };
    }

    const packageName = packagesData.packages.find(
      (packageDetail) =>
        packageDetail.id_package === activeTrial.id_package_trial
    );

    const originalPackageName = packagesData.packages.find(
      (packageDetail) =>
        packageDetail.id_package === activeTrial.id_package_trial_original
    );

    const startDate = activeTrial.ts_trial_package_start
      ? moment(activeTrial.ts_trial_package_start).format('MM/DD/YYYY')
      : 'N/A';
    const endDate = activeTrial.ts_trial_package_start
      ? moment(activeTrial.ts_trial_package_end).format('MM/DD/YYYY')
      : 'N/A';

    return {
      packageName: packageName?.name ?? 'N/A',
      originalPackageName: originalPackageName?.name ?? 'N/A',
      packageId: packageName?.id_package,
      originalPackageId: originalPackageName?.id_package,
      startDate,
      endDate,
      isActive: true,
    };
  }, [userTrialsInfo, packagesData]);

  const loginHistoryTableData = useMemo(() => {
    if (!loginHistoryData || loginHistoryData?.login_attempts.length === 0) {
      return [];
    }

    return loginHistoryData.login_attempts.map((event) => ({
      date: event.ts_event,
      status: event.event_type,
      message: event.msg,
      details: getLoginEventDetails(event.properties),
    }));
  }, [loginHistoryData]);

  return {
    isLoading,
    id: userId,
    user: data?.user,
    userTrialInfo,
    loginHistory,
    loginHistoryTableData,
    packageHistory,
    refetchUserQuery: refetchUserInformation,
    packageHistorySource,
  };
};
